import { ChevronDown, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { ChapterPlan } from "../jobDetailHelpers";

/**
 * Story-chapter shell (Issue #768). An "open" chapter renders its content
 * directly — the current stage's block is always visible. A "receipt"
 * (finished stage) or "ghost" (future stage) compresses to one line; clicking
 * the line expands the content below it. The caller owns the open state so
 * receipt expansion can persist per user (useUserPreference) and so a
 * StageStrip pill click can force a chapter open.
 */
export function ChapterShell({
    plan,
    open,
    onToggle,
    children,
}: {
    plan: ChapterPlan;
    /** Expanded state for receipt/ghost chapters; ignored when state==="open". */
    open: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}) {
    if (plan.state === "open") return <>{children}</>;
    const Chevron = open ? ChevronDown : ChevronRight;
    return (
        <div className="space-y-4">
            <Card>
                <button
                    type="button"
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm"
                    onClick={onToggle}
                    aria-expanded={open}
                    data-testid={`chapter-${plan.key}`}
                >
                    {plan.state === "receipt" ? (
                        <span className="font-bold text-emerald-600" aria-hidden>
                            ✓
                        </span>
                    ) : (
                        <span className="text-muted-foreground" aria-hidden>
                            ◻
                        </span>
                    )}
                    <span
                        className={cn(
                            "min-w-0 flex-1 truncate",
                            plan.state === "receipt" ? "font-semibold text-foreground/80" : "text-muted-foreground",
                        )}
                    >
                        {plan.line}
                    </span>
                    <Chevron className="h-4 w-4 shrink-0 text-foreground/40" />
                </button>
            </Card>
            {open && children}
        </div>
    );
}
